import { istDate, istToday } from "@/lib/istDate";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldCheck, FileArrowDown, Scissors, CheckCircle, ArrowClockwise,
  User as UserIcon, Package
} from "@phosphor-icons/react";

/**
 * Operators page — dedicated admin-only audit view for machine operators.
 *
 * Shows per-operator "machine board" cards (each operator effectively runs one
 * machine in Shobhit Creation) with counts and recent activity. Below is the
 * full chronological log with filters so the admin can trace exactly which
 * operator downloaded which cutting file / marked cutting done.
 */
const KIND_META = {
  cutting_file_download: {
    label: "DOWNLOADED FILE",
    icon: FileArrowDown,
    tone: "text-cyan-300",
    bg: "bg-cyan-500/10 border-cyan-500/40",
  },
  cutting_started: {
    label: "CUTTING STARTED",
    icon: Scissors,
    tone: "text-orange-300",
    bg: "bg-orange-500/10 border-orange-500/40",
  },
  cutting_done: {
    label: "CUTTING DONE",
    icon: CheckCircle,
    tone: "text-emerald-300",
    bg: "bg-emerald-500/10 border-emerald-500/40",
  },
};

const RANGE_OPTIONS = [
  { key: "today", label: "TODAY" },
  { key: "week", label: "THIS WEEK" },
  { key: "month", label: "THIS MONTH" },
  { key: "all", label: "ALL TIME" },
  { key: "custom", label: "CUSTOM" },
];

function _todayLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return istDate(d);
}

// Compute lower bound (ISO) for a named range.
function rangeStart(key) {
  const now = new Date();
  if (key === "today") {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return d.toISOString();
  }
  if (key === "week") {
    const d = new Date(now); d.setDate(d.getDate() - 7);
    return d.toISOString();
  }
  if (key === "month") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return d.toISOString();
  }
  return null;
}

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function Operators({ onOpenOrder }) {
  const [rawRows, setRawRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState("today");
  const [customFrom, setCustomFrom] = useState(_todayLocal());
  const [customTo, setCustomTo] = useState(_todayLocal());
  const [kindFilter, setKindFilter] = useState("all");
  const [q, setQ] = useState("");
  const [activeOperator, setActiveOperator] = useState(null); // when clicked a card, filter events

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/staff-activity", { params: { limit: 1000 } });
      setRawRows(data.events || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter by selected time range on the client so the operator cards + log stay in sync.
  const rangeRows = useMemo(() => {
    if (range === "custom") {
      // Inclusive both ends. Empty strings pass everything.
      const lo = customFrom ? `${customFrom}T00:00:00` : "";
      const hi = customTo   ? `${customTo}T23:59:59.999` : "\uffff";
      return rawRows.filter((r) => {
        const at = r.at || "";
        return (!lo || at >= lo) && at <= hi;
      });
    }
    const lo = rangeStart(range);
    if (!lo) return rawRows;
    return rawRows.filter((r) => (r.at || "") >= lo);
  }, [rawRows, range, customFrom, customTo]);

  const perOperator = useMemo(() => {
    const m = new Map();
    for (const r of rangeRows) {
      const key = r.by || "unknown";
      if (!m.has(key)) {
        m.set(key, {
          email: key,
          name: r.by_name || key,
          role: r.by_role || "",
          downloads: 0,
          cutting_started: 0,
          cutting_done: 0,
          sqft_cut: 0,
          total: 0,
          last_at: r.at,
        });
      }
      const b = m.get(key);
      b.total += 1;
      if (r.kind === "cutting_file_download") b.downloads += 1;
      if (r.kind === "cutting_started") b.cutting_started += 1;
      if (r.kind === "cutting_done") {
        b.cutting_done += 1;
        b.sqft_cut += Number(r.total_sqft || 0);
      }
      if (!b.last_at || r.at > b.last_at) b.last_at = r.at;
    }
    return [...m.values()]
      .map(b => ({ ...b, sqft_cut: Math.round(b.sqft_cut * 1000) / 1000 }))
      .sort((a, b) => (b.sqft_cut - a.sqft_cut) || (b.total - a.total));
  }, [rangeRows]);

  const visibleRows = useMemo(() => {
    let out = rangeRows;
    if (activeOperator) out = out.filter((r) => (r.by || "") === activeOperator);
    if (kindFilter !== "all") out = out.filter((r) => r.kind === kindFilter);
    if (q.trim()) {
      const rx = q.trim().toLowerCase();
      out = out.filter((r) =>
        (r.by_name || "").toLowerCase().includes(rx) ||
        (r.by || "").toLowerCase().includes(rx) ||
        (r.order_number || "").toLowerCase().includes(rx) ||
        (r.customer_name || "").toLowerCase().includes(rx)
      );
    }
    return out;
  }, [rangeRows, activeOperator, kindFilter, q]);

  return (
    <div className="space-y-5" data-testid="operators-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// OPERATORS</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-white mt-1 flex items-center gap-3">
            <ShieldCheck size={28} className="text-orange-400" weight="fill" />
            Machine Operators
          </h1>
          <div className="text-sm text-slate-400 mt-1">
            Har machine operator ne kaunsa order download kiya, kaunsa cutting start karke complete kiya — sab yahan track hota hai.
          </div>
        </div>
        <Button
          onClick={load}
          disabled={busy}
          className="rounded-sm h-10 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
          data-testid="operators-refresh"
        >
          <ArrowClockwise size={14} className={busy ? "animate-spin mr-2" : "mr-2"} />
          Refresh
        </Button>
      </div>

      {/* Range chips */}
      <div className="surface p-3 rounded-sm flex flex-wrap items-center gap-3">
        <div className="text-[10px] font-mono-num tracking-widest text-slate-500">RANGE</div>
        <div className="flex items-center gap-1 surface-2 p-1 rounded-sm" data-testid="operators-range-tabs">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={
                "px-3 py-1.5 rounded-sm text-[10px] font-mono-num tracking-widest transition-colors " +
                (range === r.key ? "bg-orange-500 text-white" : "text-slate-400 hover:text-slate-200")
              }
              data-testid={`operators-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {/* Custom date pickers — appear only when CUSTOM chip is active */}
        {range === "custom" && (
          <div
            className="flex items-center gap-2 pl-2 border-l border-slate-700"
            data-testid="operators-custom-range"
          >
            <input
              type="date"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="bg-slate-900/60 border border-slate-600 focus:border-orange-500 focus:outline-none rounded-sm h-8 px-2 text-xs font-mono-num text-slate-200"
              data-testid="operators-custom-from"
              title="Start date (inclusive)"
            />
            <span className="text-slate-500 text-[10px] font-mono-num">→</span>
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              max={_todayLocal()}
              onChange={(e) => setCustomTo(e.target.value)}
              className="bg-slate-900/60 border border-slate-600 focus:border-orange-500 focus:outline-none rounded-sm h-8 px-2 text-xs font-mono-num text-slate-200"
              data-testid="operators-custom-to"
              title="End date (inclusive)"
            />
            <button
              onClick={() => {
                const t = _todayLocal();
                setCustomFrom(t); setCustomTo(t);
              }}
              className="text-[10px] font-mono-num tracking-widest text-orange-400 hover:text-orange-300 px-2 h-8"
              data-testid="operators-custom-reset"
              title="Reset to today"
            >
              TODAY
            </button>
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          <div
            className="hidden sm:flex items-center gap-2 px-3 h-8 rounded-sm bg-emerald-500/10 border border-emerald-500/30"
            data-testid="total-sqft-cut-card"
            title="Total sqft cut by all operators in the selected range"
          >
            <span className="text-[9px] font-mono-num text-emerald-400/80 tracking-widest">TOTAL SQ.FT</span>
            <span className="text-base font-heading text-emerald-300 font-bold">
              {perOperator.reduce((s, o) => s + (o.sqft_cut || 0), 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="text-[11px] font-mono-num text-slate-500">
            {perOperator.length} operator{perOperator.length === 1 ? "" : "s"} · {rangeRows.length} events
          </div>
        </div>
      </div>

      {/* Operator cards ("machine boards") */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {perOperator.length === 0 && !busy && (
          <div className="col-span-full surface p-8 rounded-sm text-center text-slate-500 text-sm">
            Is range me abhi tak koi operator activity nahi hui.
          </div>
        )}
        {perOperator.map((op, i) => {
          const isActive = activeOperator === op.email;
          return (
            <button
              key={op.email}
              onClick={() => setActiveOperator(isActive ? null : op.email)}
              className={
                "surface p-5 rounded-sm text-left transition-all border-2 " +
                (isActive
                  ? "border-orange-500 bg-orange-500/5"
                  : "border-transparent hover:border-slate-700")
              }
              data-testid={`operator-card-${op.email}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono-num tracking-widest text-slate-500">
                    MACHINE {String.fromCharCode(65 + i)} · {op.role || "OPERATOR"}
                  </div>
                  <div className="font-heading text-2xl font-black text-white mt-1 flex items-center gap-2">
                    <div className="w-9 h-9 rounded-sm bg-orange-500/20 border border-orange-500/50 text-orange-300 flex items-center justify-center">
                      <UserIcon size={16} weight="bold" />
                    </div>
                    <span className="truncate">{op.name}</span>
                  </div>
                  <div className="text-[10px] font-mono-num text-slate-500 mt-0.5 truncate">
                    {op.email}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-black font-mono-num text-orange-400 text-4xl leading-none">
                    {op.total}
                  </div>
                  <div className="text-[9px] font-mono-num text-slate-500 tracking-widest">EVENTS</div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                <div className="surface-2 rounded-sm py-2">
                  <div className="flex items-center justify-center gap-1 text-cyan-300 text-xs">
                    <FileArrowDown size={12} weight="bold" /> {op.downloads}
                  </div>
                  <div className="text-[9px] font-mono-num text-slate-500 tracking-widest mt-0.5">DXF</div>
                </div>
                <div className="surface-2 rounded-sm py-2">
                  <div className="flex items-center justify-center gap-1 text-orange-300 text-xs">
                    <Scissors size={12} weight="bold" /> {op.cutting_started}
                  </div>
                  <div className="text-[9px] font-mono-num text-slate-500 tracking-widest mt-0.5">STARTED</div>
                </div>
                <div className="surface-2 rounded-sm py-2">
                  <div className="flex items-center justify-center gap-1 text-emerald-300 text-xs">
                    <CheckCircle size={12} weight="bold" /> {op.cutting_done}
                  </div>
                  <div className="text-[9px] font-mono-num text-slate-500 tracking-widest mt-0.5">DONE</div>
                </div>
                <div
                  className="surface-2 rounded-sm py-2 bg-emerald-500/5 border border-emerald-500/20"
                  data-testid={`op-sqft-${op.email}`}
                  title="Total sqft this operator has closed in the selected range"
                >
                  <div className="text-emerald-300 text-xs font-bold font-mono-num leading-none">
                    {op.sqft_cut > 0 ? op.sqft_cut.toLocaleString("en-IN") : "0"}
                  </div>
                  <div className="text-[9px] font-mono-num text-emerald-500/80 tracking-widest mt-0.5">SQ.FT</div>
                </div>
              </div>

              <div className="mt-3 text-[10px] font-mono-num text-slate-500">
                Last active: {fmtWhen(op.last_at)}
              </div>
              {isActive && (
                <div className="mt-2 text-[10px] font-mono-num text-orange-400 tracking-widest">
                  ↓ FILTERING LOG BY THIS OPERATOR
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="surface p-4 rounded-sm flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">SEARCH</Label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Operator, order #, customer…"
            className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
            data-testid="operators-search"
          />
        </div>
        <div className="flex items-center gap-1 surface-2 p-1 rounded-sm">
          {[
            { k: "all", label: "ALL" },
            { k: "cutting_file_download", label: "DOWNLOADS" },
            { k: "cutting_started", label: "STARTED" },
            { k: "cutting_done", label: "DONE" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setKindFilter(f.k)}
              className={
                "px-3 py-1.5 rounded-sm text-[10px] font-mono-num tracking-widest transition-colors " +
                (kindFilter === f.k ? "bg-orange-500 text-white" : "text-slate-400 hover:text-slate-200")
              }
              data-testid={`operators-kind-${f.k}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {activeOperator && (
          <Button
            onClick={() => setActiveOperator(null)}
            variant="outline"
            className="rounded-sm h-9 border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
            data-testid="operators-clear-filter"
          >
            Clear operator filter
          </Button>
        )}
      </div>

      {/* Events log */}
      <div className="surface rounded-sm overflow-hidden" data-testid="operators-log">
        <div className="flex items-center justify-between px-3 py-2 bg-slate-900/60">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400">
            // ACTIVITY LOG · {visibleRows.length} EVENT{visibleRows.length === 1 ? "" : "S"}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-slate-500 font-mono-num text-[10px] tracking-widest bg-slate-900/40">
            <tr>
              <th className="text-left py-2 px-3">WHEN</th>
              <th className="text-left">ACTION</th>
              <th className="text-left">OPERATOR</th>
              <th className="text-left">ORDER</th>
              <th className="text-left">CUSTOMER</th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500">Loading…</td></tr>
            )}
            {!busy && visibleRows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm">
                Koi matching event nahi mila.
              </td></tr>
            )}
            {!busy && visibleRows.map((r) => {
              const meta = KIND_META[r.kind] || {};
              const Icon = meta.icon;
              return (
                <tr
                  key={r.id}
                  className="border-t border-slate-800 hover:bg-slate-800/30 cursor-pointer"
                  onClick={() => onOpenOrder?.(r.order_id)}
                  data-testid={`operators-log-row-${r.id}`}
                >
                  <td className="py-2.5 px-3 text-slate-400 font-mono-num text-xs whitespace-nowrap">
                    {fmtWhen(r.at)}
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-mono-num tracking-widest px-2 py-0.5 rounded-sm border ${meta.bg} ${meta.tone}`}>
                      {Icon && <Icon size={11} weight="bold" />} {meta.label || r.kind.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="text-slate-100">{r.by_name || r.by || "—"}</div>
                    <div className="text-[10px] font-mono-num text-slate-500">{r.by}</div>
                  </td>
                  <td className="font-mono-num text-orange-400">
                    <span className="inline-flex items-center gap-1">
                      <Package size={11} /> #{r.order_number}
                    </span>
                  </td>
                  <td className="text-slate-200 truncate max-w-[220px]">{r.customer_name || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
